import { useState } from "react";
import { Link } from "wouter";
import { apiFetch } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: email.trim() }),
      });
      setSent(true);
    } catch (err: any) {
      setError(err?.message ?? "Could not send reset link.");
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-brand-100 flex items-center justify-center text-2xl">
            📬
          </div>
          <h1 className="mt-5 text-2xl font-bold text-slate-900">
            Check your email
          </h1>
          <p className="mt-2 text-slate-600">
            If an account exists for <strong>{email}</strong>, we sent a
            password-reset link. It expires in 60 minutes.
          </p>
          <p className="mt-8 text-sm text-slate-500">
            <Link href="/login" className="text-brand-700 hover:underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-slate-900">Reset password</h1>
          <p className="mt-2 text-slate-600">
            Enter your email and we'll send you a link to set a new password.
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-sm rounded-xl p-6 border border-slate-200"
        >
          <label className="block">
            <span className="text-sm font-medium text-slate-700">
              Email address
            </span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              autoFocus
              autoComplete="username"
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
            />
          </label>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={submitting || !email.trim()}
            className="mt-5 w-full rounded-lg bg-brand-600 px-4 py-2.5 text-white font-medium hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {submitting ? "Sending…" : "Send reset link"}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-slate-500">
          <Link href="/login" className="hover:text-slate-700">
            ← Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
